const PROBE_KEY = "__storage_probe__";

class MemoryStorage implements Storage {
  private readonly items = new Map<string, string>();

  public get length(): number {
    return this.items.size;
  }

  public clear(): void {
    this.items.clear();
  }

  public getItem(key: string): string | null {
    return this.items.get(key) ?? null;
  }

  public key(index: number): string | null {
    return Array.from(this.items.keys())[index] ?? null;
  }

  public removeItem(key: string): void {
    this.items.delete(key);
  }

  public setItem(key: string, value: string): void {
    this.items.set(key, String(value));
  }
}

const fallbacks: Partial<Record<"localStorage" | "sessionStorage", Storage>> = {};

/**
 * The browser storage, or an in-memory one for this page load when the browser blocks it: an embedded app
 * runs in a third-party iframe, where incognito and strict privacy modes deny storage (App Store 1.1.1).
 */
export const getSafeStorage = (type: "localStorage" | "sessionStorage", view: Window | null = window): Storage => {
  try {
    const storage = view[type];
    storage.setItem(PROBE_KEY, PROBE_KEY);
    storage.removeItem(PROBE_KEY);
    return storage;
  } catch {
    return (fallbacks[type] ??= new MemoryStorage());
  }
};

/** False for the in-memory fallback, whose values are gone after a reload. */
export const isPersistentStorage = (storage: Storage): boolean => !(storage instanceof MemoryStorage);
